import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const GOOGLE_ERRORS = {
  google_cancelled: "Sign-in was cancelled. Give it another try when you're ready.",
};

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser, setLoading } = useAuth();
  const [remember, setRemember] = useState(true);
  const [mode, setMode] = useState("signin"); // signin | signup | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const next = params.get("next");
    if (next && next.startsWith("/")) {
      try { sessionStorage.setItem("bf_post_login_next", next); } catch { /* storage blocked */ }
    }
    const err = params.get("error");
    if (err) setError(GOOGLE_ERRORS[err] || "We couldn't complete sign-in. Please try again.");
  }, [location.search]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const params = new URLSearchParams({ remember: remember ? "1" : "0" });
    try {
      const next = sessionStorage.getItem("bf_post_login_next");
      if (next && next.startsWith("/")) params.set("next", next);
    } catch { /* noop */ }
    const ref = localStorage.getItem("bfa_ref_code");
    if (ref) params.set("ref", ref);
    window.location.href = `${window.location.origin}/api/auth/google/login?${params.toString()}`;
  };

  const afterAuth = (user) => {
    localStorage.removeItem("bfa_ref_code");
    setUser(user);
    setLoading(false);
    let dest = user?.station_id ? "/dashboard" : "/onboarding";
    try {
      const next = sessionStorage.getItem("bf_post_login_next");
      if (next && next.startsWith("/")) {
        dest = next;
        sessionStorage.removeItem("bf_post_login_next");
      }
    } catch { /* storage blocked */ }
    navigate(dest, { replace: true, state: { user } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const res = await api.post("/auth/forgot-password", { email });
        setNotice(res.data?.message || "If an account exists for that email, a reset link is on its way.");
      } else if (mode === "signup") {
        const ref = localStorage.getItem("bfa_ref_code") || undefined;
        const res = await api.post("/auth/register", { name, email, password, remember, ref });
        afterAuth(res.data.user);
      } else {
        const res = await api.post("/auth/login", { email, password, remember });
        afterAuth(res.data.user);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setNotice("");
  };

  const inputCls = "w-full rounded-md border border-line bg-bg-elevated px-4 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60 transition";

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg text-ink-primary relative overflow-hidden">
      <div className="absolute inset-0 scan-line opacity-50" />
      <div className="absolute top-0 left-0 right-0 p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-secondary hover:text-ink-primary transition" data-testid="back-home">
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>
      </div>

      <div className="relative w-full max-w-md p-8">
        <div className="rounded-2xl border border-line bg-bg-surface p-8 lift">
          <div className="flex items-center mb-6">
            <img src="/brand/broadcastflowai-hero.png" alt="BroadcastFlow AI" className="h-24 w-auto object-contain" />
          </div>

          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber mb-3">
            // STUDIO ACCESS
          </div>
          <h1 className="font-display text-3xl font-black tracking-tighter mb-2">
            {mode === "signup" ? "Create your station account" : mode === "forgot" ? "Reset your password" : "Sign in to your station"}
          </h1>
          <p className="text-sm text-ink-secondary mb-6">
            {mode === "forgot"
              ? "Enter your email and we'll send you a link to choose a new password."
              : "Tune in to the control room."}
          </p>

          {error && (
            <div className="mb-5 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-300" data-testid="login-error-banner">
              {error}
            </div>
          )}
          {notice && (
            <div className="mb-5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-xs text-emerald-300" data-testid="login-notice-banner">
              {notice}
            </div>
          )}

          {mode !== "forgot" && (
            <>
              <button
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
                className="w-full flex items-center justify-center gap-3 rounded-md border border-line bg-bg-elevated px-6 py-3 font-medium hover:bg-bg-surface transition group"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Continue with Google</span>
              </button>

              <div className="flex items-center gap-3 my-6">
                <div className="h-px flex-1 bg-line" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">or use email</span>
                <div className="h-px flex-1 bg-line" />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="password-auth-form">
            {mode === "signup" && (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
                autoComplete="name"
                className={inputCls}
                data-testid="auth-name-input"
              />
            )}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@station.com"
              required
              autoComplete="email"
              className={inputCls}
              data-testid="auth-email-input"
            />
            {mode !== "forgot" && (
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "signup" ? "Password (min 8 characters)" : "Password"}
                required
                minLength={8}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                className={inputCls}
                data-testid="auth-password-input"
              />
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-btn"
              className="w-full flex items-center justify-center gap-2 rounded-md bg-brand-amber px-6 py-2.5 text-black font-semibold text-sm hover:bg-brand-amber/90 disabled:opacity-60 transition"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in"}
            </button>
          </form>

          <div className="mt-4 flex items-center justify-between text-xs">
            {mode === "signin" && (
              <>
                <button type="button" onClick={() => switchMode("forgot")} className="text-ink-muted hover:text-ink-primary transition" data-testid="forgot-password-link">
                  Forgot password?
                </button>
                <button type="button" onClick={() => switchMode("signup")} className="text-brand-amber hover:text-brand-amber/80 transition font-medium" data-testid="switch-to-signup">
                  Create an account
                </button>
              </>
            )}
            {mode === "signup" && (
              <button type="button" onClick={() => switchMode("signin")} className="text-brand-amber hover:text-brand-amber/80 transition font-medium" data-testid="switch-to-signin">
                Already have an account? Sign in
              </button>
            )}
            {mode === "forgot" && (
              <button type="button" onClick={() => switchMode("signin")} className="text-brand-amber hover:text-brand-amber/80 transition font-medium" data-testid="back-to-signin">
                ← Back to sign in
              </button>
            )}
          </div>

          {mode !== "forgot" && (
            <label className="mt-5 flex items-center gap-2 text-xs text-ink-secondary cursor-pointer select-none" data-testid="login-remember-label">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-3.5 w-3.5 accent-brand-amber"
                data-testid="login-remember-checkbox"
              />
              Stay signed in on this device <span className="text-ink-muted">(30 days)</span>
            </label>
          )}

          <div className="mt-8 pt-6 border-t border-line text-xs text-ink-muted font-mono">
            <div className="flex items-center justify-between">
              <span>// SECURE OAUTH 2.0</span>
              <span className="text-brand-amber">ENCRYPTED</span>
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-ink-muted mt-6">
          By signing in you agree to our Terms & Privacy Policy.
        </p>
      </div>
    </div>
  );
}
